import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Avatar
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import { createDataset, uploadDatasetIconFromUrl } from '../services/api/datasetService';

/**
 * CreateDatasetDialog - Dialog for creating a new dataset
 *
 * Props:
 * - open: Boolean, controls dialog visibility
 * - onClose: Function called when dialog closes
 * - onCreated: Function called when dataset successfully created
 */
export default function CreateDatasetDialog({ open, onClose, onCreated }) {
  const [datasetId, setDatasetId] = useState('');
  const [datasetName, setDatasetName] = useState('');
  const [description, setDescription] = useState('');
  const [iconUrl, setIconUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function handleClose() {
    if (!submitting) {
      setDatasetId('');
      setDatasetName('');
      setDescription('');
      setIconUrl('');
      setError(null);
      onClose();
    }
  }

  async function handleSubmit() {
    if (!datasetId.trim()) {
      setError('Dataset ID is required');
      return;
    }

    if (!datasetName.trim()) {
      setError('Dataset name is required');
      return;
    }

    // Validate dataset_id format
    if (!/^[A-Z0-9]{2,6}$/.test(datasetId.trim())) {
      setError('Dataset ID must be 2-6 uppercase letters/numbers (e.g., MI5, SAMP1)');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const trimmedDatasetId = datasetId.trim().toUpperCase();

      // Create dataset (without icon initially)
      const datasetData = {
        dataset_id: trimmedDatasetId,
        dataset_name: datasetName.trim(),
        description: description.trim() || undefined
      };

      const newDataset = await createDataset(datasetData);

      // If icon URL provided, send to backend for download and upload (bypasses CORS)
      if (iconUrl.trim()) {
        try {
          await uploadDatasetIconFromUrl(trimmedDatasetId, iconUrl.trim());
          console.log(`Icon uploaded successfully for dataset ${trimmedDatasetId}`);
        } catch (iconError) {
          console.error('Failed to upload icon:', iconError);
          // Don't fail the whole operation - dataset is already created
          setError(`Dataset created, but icon upload failed: ${iconError.message}`);
        }
      }

      // Reset form
      setDatasetId('');
      setDatasetName('');
      setDescription('');
      setIconUrl('');

      // Notify parent
      onCreated(newDataset);
      onClose();
    } catch (err) {
      console.error('Failed to create dataset:', err);
      setError(err.message || 'Failed to create dataset');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Create New Dataset</DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          margin="dense"
          label="Dataset ID"
          fullWidth
          required
          value={datasetId}
          onChange={(e) => setDatasetId(e.target.value.toUpperCase())}
          disabled={submitting}
          placeholder="e.g., MI5, SAMP1, HOP"
          helperText="2-6 uppercase letters/numbers"
          inputProps={{ maxLength: 6, style: { textTransform: 'uppercase' } }}
        />

        <TextField
          margin="dense"
          label="Dataset Name"
          fullWidth
          required
          value={datasetName}
          onChange={(e) => setDatasetName(e.target.value)}
          disabled={submitting}
          placeholder="e.g., MI5 Personnel, London Office Staff"
          helperText="Human-readable name for this dataset"
        />

        <TextField
          margin="dense"
          label="Description"
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={submitting}
          placeholder="Optional description of this dataset"
        />

        <TextField
          margin="dense"
          label="Icon URL"
          fullWidth
          value={iconUrl}
          onChange={(e) => setIconUrl(e.target.value)}
          disabled={submitting}
          placeholder="https://example.com/icon.png (optional)"
          helperText="URL to an icon image for this dataset"
        />

        {/* Icon Preview */}
        {iconUrl && (
          <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Preview:
            </Typography>
            <Avatar
              src={iconUrl}
              variant="rounded"
              sx={{
                width: 48,
                height: 48,
                bgcolor: 'grey.100'
              }}
            >
              <ImageIcon sx={{ color: 'text.disabled' }} />
            </Avatar>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={submitting || !datasetId.trim() || !datasetName.trim()}
        >
          {submitting ? <CircularProgress size={24} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
